import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { alertsQuery, inventoryQuery, summaryQuery } from '@/lib/queries';

interface KpiCardProps {
  label: string;
  value: ReactNode;
  tooltip?: string;
  to?: string;
  highlight?: boolean;
}

function KpiCard({ label, value, tooltip, to, highlight }: KpiCardProps) {
  const inner = (
    <div
      className={`flex flex-col items-center justify-center px-6 py-3 border-r border-ndd-800 last:border-r-0 ${
        highlight ? 'text-orange-400' : 'text-white'
      }`}
    >
      <span className="text-xs font-medium text-ndd-400">{label}</span>
      <span className="text-2xl font-bold">{value}</span>
    </div>
  );

  const wrapped = tooltip ? (
    <Tooltip>
      <TooltipTrigger>{inner}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    inner
  );

  if (to) {
    return <Link to={to as '/alerts'}>{wrapped}</Link>;
  }

  return wrapped;
}

export function KpiBar() {
  const { data: summary } = useQuery(summaryQuery);
  const { data: alerts } = useQuery(alertsQuery);
  const { data: inventory } = useQuery(inventoryQuery());

  const totalHerd = summary?.herd.total ?? '—';
  const milking = summary?.herd.milking ?? '—';
  const feedValue = inventory
    ? `$${inventory.total_value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
    : '—';

  const topConcern = summary?.inventory_top_concerns[0];
  const csRemaining = topConcern ? `${topConcern.days_remaining}d` : '—';
  const csLabel = topConcern?.name ? `${topConcern.name}` : 'CS Remaining';

  const alertCount = alerts?.alerts.length ?? 0;

  return (
    <TooltipProvider>
      <div className="flex h-16 shrink-0 items-stretch border-b border-ndd-800 bg-ndd-950">
        <KpiCard label="Total Herd" value={totalHerd} tooltip="Total animals in register" />
        <KpiCard label="Milking" value={milking} tooltip="Currently milking" />
        <KpiCard label="Feed Value" value={feedValue} tooltip="Total inventory value" />
        <KpiCard
          label={csLabel}
          value={csRemaining}
          tooltip={
            topConcern?.below_threshold ? 'Below alert threshold!' : 'Days of supply remaining'
          }
          highlight={!!topConcern?.below_threshold}
        />
        <KpiCard
          label="Alerts"
          value={
            <span className="flex items-center gap-1">
              {alertCount > 0 && <AlertTriangle size={16} className="text-orange-400" />}
              {alertCount}
            </span>
          }
          to="/alerts"
          highlight={alertCount > 0}
        />
      </div>
    </TooltipProvider>
  );
}
